import type { IndicatorReferenceValues } from '../../../indicators/indicatorReferenceLines';

export const hasAnyReferenceValue = (
  values: IndicatorReferenceValues
): boolean =>
  [
    values.upperTolerance,
    values.lowerTolerance,
    values.upperAppetite,
    values.lowerAppetite,
  ].some((value) => value != null);

export type ToleranceToggleState = {
  /** Whether the show/hide toggle should be offered in the widget settings. */
  canToggle: boolean;
  /** Whether tolerance/appetite reference lines should be drawn right now. */
  showLines: boolean;
};

/**
 * Derives the tolerance toggle's availability and the line-rendering decision
 * from live widget data + the persisted setting (CUS-143). The toggle is only
 * offered for a single indicator with reference values while the flag is on;
 * lines render only when additionally the persisted toggle is on. Crucially,
 * `canToggle` depends on live data, never the persisted value — so the stored
 * `showTolerances` is untouched across a single→multi→single transition and the
 * toggle reappears in its prior state.
 */
export const resolveToleranceToggleState = ({
  tolerancesEnabled,
  isSingleIndicator,
  hasReferenceValues,
  storedShowTolerances,
}: {
  tolerancesEnabled: boolean;
  isSingleIndicator: boolean;
  hasReferenceValues: boolean;
  storedShowTolerances: boolean | undefined;
}): ToleranceToggleState => {
  const canToggle =
    tolerancesEnabled && isSingleIndicator && hasReferenceValues;

  return { canToggle, showLines: canToggle && storedShowTolerances === true };
};

/**
 * The value to persist on save. When the toggle is offered, take the form's
 * value; otherwise keep whatever was stored so a save (e.g. a filter change
 * that hides the toggle) can't silently reset it.
 */
export const resolveNextShowTolerances = ({
  canToggle,
  formShowTolerances,
  storedShowTolerances,
}: {
  canToggle: boolean;
  formShowTolerances: boolean | undefined;
  storedShowTolerances: boolean | undefined;
}): boolean =>
  canToggle ? formShowTolerances === true : storedShowTolerances === true;
